<template lang="pug">
div.commentary-group-inner
  VvBlockQuote(
    v-if="verseQuote"
    :secondary="true"
    :p="verseQuote"
    :cite="verseCite"
  )
  div.commentary-doc.my-2(
    v-for="doc in docs"
    :key="doc.nid"
  )
    CommentTeaserCard.my-2(
      v-if="doc.comment"
      :heading="doc.comment.heading"
      :teaser="doc.comment.teaser"
      :ranges="doc.comment.ranges"
      :detail-level-name="doc.comment.detailLevel.name[locale] ?? '?'"
      :detail-level-num-dots="numDotsPerDetailLevelWeight[doc.comment.detailLevel.weight] ?? 0"
      :commentary-type-name="commentaryTypeName"
      :commentary-type-short-name="commentaryTypeShortName"
      :nid="doc.nid"
      :local-tid="localTid"
      :is-selected="selected.includes(doc.nid)"
      :show-default-reading-list="true"
      :show-reading-list-menu="true"
      :language="doc.comment.language"
    )
    CommentaryImage(
      v-else-if="doc.image"
      :title="doc.image.title"
      :description="doc.image.description"
      :large-url="doc.image.styles.large"
      :small-url="doc.image.styles.small"
      :placeholder-data="doc.image.styles.placeholder"
      :original-url="doc.image.styles.original"
    )
</template>

<script setup lang="ts">
import type { PartialVerse } from "@rsc/scripture-util";
import { useI18n } from "vue-i18n";
import { computed } from "vue";
import { numDotsPerDetailLevelWeight } from "~/static/taxonomy/detailLevels";
import type { Props } from "~/components/CommentaryGroupInner.types";
import {
  computeDocList,
  mergeMissingTranslationOnDocComputed,
  type DocComputed,
} from "./CommentaryGroup.util";

const i18n = useI18n();
const locale = i18n.locale;

const props = withDefaults(defineProps<Props>(), {
  selected: () => [],
});

const docs = computed<DocComputed[]>(() =>
  mergeMissingTranslationOnDocComputed(
    computeDocList(toRaw(props.doclist.docs)),
    props.translationsByNid,
  ),
);

const partialVerse = computed<PartialVerse | null>(() => {
  return (
    (props.verse?.partialVerses || []).find(
      (p) => p.label === props.versePart,
    ) || null
  );
});

const verseQuote = computed<string>(() => {
  if (!props.showVerseQuote) {
    return "";
  }

  const pv = partialVerse.value;
  if (!props.verse || !pv) {
    return "";
  }
  const partCode = pv.label;
  const partialVerses = props.verse.partialVerses;
  const index = partialVerses.findIndex(
    (partObj) => partObj.label === partCode,
  );
  const notFirst = index !== 0;
  const notLast = index !== partialVerses.length - 1;

  const firstEllipsis = notFirst ? "…" : "";
  const lastEllipsis = notLast ? "…" : "";

  // Quote partial verse.
  return (
    firstEllipsis + props.verse.getFormattedPartText(pv, []) + lastEllipsis
  );
});

const verseCite = computed<string>(() => {
  const pv = partialVerse.value;
  if (!props.verse || !pv) {
    return "";
  }

  return props.verse.renderReference(false, pv.label, true);
});
</script>
